// src/pages/drone/buah-alami/formula/calculationFormula.js

/**
 * Fungsi untuk menghitung week berdasarkan tanggal terbang sesuai WEEKNUM Excel
 * WEEKNUM(date, 1): Minggu dimulai hari Minggu, week 1 adalah 1 Januari
 * @param {string|Date} tanggalTerbang - Tanggal terbang (string format atau Date object)
 * @returns {number} Week number (1-53) sesuai Excel WEEKNUM atau '-' jika tidak valid
 */
export const calculateWeek = (tanggalTerbang) => {
    try {
        if (!tanggalTerbang || tanggalTerbang === '-') return '-';
        
        // Parse tanggal dari berbagai format
        let date;
        
        // Coba parse dari string
        if (typeof tanggalTerbang === 'string') {
            // Cek format DD-MM-YYYY
            const parts = tanggalTerbang.split('-');
            if (parts.length === 3) {
                // Format DD-MM-YYYY
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // Bulan 0-indexed
                const year = parseInt(parts[2], 10);
                
                date = new Date(year, month, day);
            } else {
                // Coba parse sebagai string tanggal standar
                date = new Date(tanggalTerbang);
            }
        } else {
            date = new Date(tanggalTerbang);
        }
        
        // Validasi tanggal
        if (isNaN(date.getTime())) {
            console.warn(`Tanggal tidak valid: ${tanggalTerbang}`);
            return '-';
        }
        
        // ALGORITMA SEDERHANA WEEKNUM Excel (return_type = 1):
        // 1. Dapatkan 1 Januari tahun yang sama
        const year = date.getFullYear();
        const jan1 = new Date(year, 0, 1); // 1 Januari tahun ini
        
        // 2. Hitung selisih hari antara tanggal dan 1 Januari
        const diffTime = date.getTime() - jan1.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        // 3. Dapatkan hari dalam minggu untuk 1 Januari (0=Minggu, 1=Senin, ..., 6=Sabtu)
        const jan1DayOfWeek = jan1.getDay();
        
        // 4. Hitung week number berdasarkan algoritma Excel WEEKNUM dengan return_type = 1
        // Formula: WEEKNUM = INT((tanggal - WEEKDAY(tanggal) + 8)/7)
        // Tapi karena kita mulai dari 1 Januari, kita hitung dengan cara ini:
        
        // Hari dalam minggu untuk tanggal target (0=Minggu, 1=Senin, ..., 6=Sabtu)
        const dateDayOfWeek = date.getDay();
        
        // Hitung week number
        // Cara sederhana: (hari dalam tahun + (7 - hari dalam minggu tanggal) + (hari dalam minggu 1 Januari - 1)) / 7
        const weekNum = Math.floor((diffDays + (7 - dateDayOfWeek) + (jan1DayOfWeek - 1)) / 7) + 1;
        
        return weekNum.toString();
        
    } catch (error) {
        console.error("Error calculating week:", error, "for date:", tanggalTerbang);
        return '-';
    }
};

/**
 * Fungsi alternatif yang lebih akurat dan sederhana untuk WEEKNUM Excel
 * Implementasi langsung dari dokumentasi Microsoft:
 * https://support.microsoft.com/en-us/office/weeknum-function-e5c43a03-b4ab-426c-b411-b18c13c75340
 */
export const calculateWeekExcel = (tanggalTerbang) => {
    try {
        if (!tanggalTerbang || tanggalTerbang === '-') return '-';
        
        let date;
        
        // Parse tanggal dari format DD-MM-YYYY
        if (typeof tanggalTerbang === 'string' && tanggalTerbang.includes('-')) {
            const parts = tanggalTerbang.split('-');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                
                // Validasi
                if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900 || year > 2100) {
                    return '-';
                }
                
                date = new Date(year, month, day);
                
                // Validasi lagi untuk kasus seperti 31 Februari
                if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
                    return '-';
                }
            } else {
                date = new Date(tanggalTerbang);
            }
        } else {
            date = new Date(tanggalTerbang);
        }
        
        if (isNaN(date.getTime())) {
            return '-';
        }
        
        // IMPLEMENTASI PERSIS WEEKNUM Excel dengan return_type = 1
        // Week dimulai hari Minggu, week 1 adalah week yang mengandung 1 Januari
        
        const year = date.getFullYear();
        
        // Dapatkan 1 Januari tahun ini
        const jan1 = new Date(year, 0, 1);
        
        // Hari dalam minggu untuk 1 Januari (0=Minggu, 1=Senin, ..., 6=Sabtu)
        const jan1DayOfWeek = jan1.getDay();
        
        // Hitung offset untuk week 1
        // Jika 1 Januari bukan Minggu, maka hari-hari sebelum Minggu pertama masih week 1
        let week1Start;
        if (jan1DayOfWeek === 0) {
            // 1 Januari adalah Minggu
            week1Start = jan1;
        } else {
            // Week 1 dimulai dari Minggu sebelum atau pada 1 Januari
            const daysToPrevSunday = jan1DayOfWeek; // Karena 0=Minggu
            week1Start = new Date(year, 0, 1 - daysToPrevSunday);
        }
        
        // Hitung selisih hari antara tanggal dan awal week 1
        const diffMs = date.getTime() - week1Start.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        // Hitung week number
        const weekNum = Math.floor(diffDays / 7) + 1;
        
        // Pastikan week number valid
        if (weekNum < 1 || weekNum > 53) {
            return '-';
        }
        
        return weekNum.toString();
        
    } catch (error) {
        console.error("Error in calculateWeekExcel:", error, "for date:", tanggalTerbang);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung total buah alami
 * @param {number|string} earlyCones - Jumlah early cones
 * @param {number|string} dryPetals - Jumlah dry petals
 * @param {number|string} buahAlami - Jumlah buah alami
 * @returns {string} Total buah alami yang diformat atau '-' jika error
 */
export const calculateTotalBuahAlami = (earlyCones, dryPetals, buahAlami) => {
    try {
        // Konversi ke number, default 0 jika tidak valid
        const earlyConesNum = parseFloat(earlyCones) || 0;
        const dryPetalsNum = parseFloat(dryPetals) || 0;
        const buahAlamiNum = parseFloat(buahAlami) || 0;
        
        const total = earlyConesNum + dryPetalsNum + buahAlamiNum;
        
        // Format dengan separator ribuan
        return total.toLocaleString('id-ID');
        
    } catch (error) {
        console.error("Error calculating total buah alami:", error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung populasi total dengan format konsisten
 * PERUBAHAN: Kembalikan ANGKA (bukan string yang sudah diformat)
 * @param {number|string} populasiPerHa - Populasi per hektar
 * @param {number|string} luasNetto - Luas netto dalam hektar
 * @returns {number|string} Populasi total sebagai angka atau '-' jika error
 */
export const calculatePopulasiTotal = (populasiPerHa, luasNetto) => {
    try {
        // Konversi ke number, default 0 jika tidak valid
        const populasiPerHaNum = parseFloat(populasiPerHa) || 0;
        const luasNettoNum = parseFloat(luasNetto) || 0;
        
        const total = populasiPerHaNum * luasNettoNum;
        
        // Kembalikan ANGKA (bukan string yang sudah diformat)
        // Formatting akan dilakukan di komponen React menggunakan formatNumber
        return total;
        
    } catch (error) {
        console.error("Error calculating populasi total:", error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung populasi total (versi tanpa pembulatan)
 * @param {number|string} populasiPerHa - Populasi per hektar
 * @param {number|string} luasNetto - Luas netto dalam hektar
 * @returns {string} Populasi total yang diformat dengan 0 desimal
 */
export const calculatePopulasiTotalExact = (populasiPerHa, luasNetto) => {
    try {
        // Konversi ke number, default 0 jika tidak valid
        const populasiPerHaNum = parseFloat(populasiPerHa) || 0;
        const luasNettoNum = parseFloat(luasNetto) || 0;
        
        const total = populasiPerHaNum * luasNettoNum;
        
        // Format dengan 0 desimal dan separator ribuan
        // Menggunakan toLocaleString dengan minimumFractionDigits dan maximumFractionDigits
        return total.toLocaleString('id-ID', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
        
    } catch (error) {
        console.error("Error calculating populasi total exact:", error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung persentase buah alami
 * @param {number|string} earlyCones - Jumlah early cones
 * @param {number|string} dryPetals - Jumlah dry petals
 * @param {number|string} buahAlami - Jumlah buah alami
 * @returns {Object} Object berisi persentase untuk masing-masing komponen
 */
export const calculatePercentageBuahAlami = (earlyCones, dryPetals, buahAlami) => {
    try {
        // Konversi ke number
        const earlyConesNum = parseFloat(earlyCones) || 0;
        const dryPetalsNum = parseFloat(dryPetals) || 0;
        const buahAlamiNum = parseFloat(buahAlami) || 0;
        
        const total = earlyConesNum + dryPetalsNum + buahAlamiNum;
        
        if (total === 0) {
            return {
                earlyConesPercentage: '0%',
                dryPetalsPercentage: '0%',
                buahAlamiPercentage: '0%',
                totalPercentage: '0%'
            };
        }
        
        // Hitung persentase
        const earlyConesPercentage = ((earlyConesNum / total) * 100).toFixed(2) + '%';
        const dryPetalsPercentage = ((dryPetalsNum / total) * 100).toFixed(2) + '%';
        const buahAlamiPercentage = ((buahAlamiNum / total) * 100).toFixed(2) + '%';
        const totalPercentage = '100%';
        
        return {
            earlyConesPercentage,
            dryPetalsPercentage,
            buahAlamiPercentage,
            totalPercentage
        };
        
    } catch (error) {
        console.error("Error calculating percentage buah alami:", error);
        return {
            earlyConesPercentage: '-',
            dryPetalsPercentage: '-',
            buahAlamiPercentage: '-',
            totalPercentage: '-'
        };
    }
};

/**
 * Fungsi untuk menghitung persentase berdasarkan populasi total
 * @param {number|string} value - Nilai yang akan dihitung persentasenya
 * @param {number|string} populasiTotal - Populasi total sebagai pembagi
 * @returns {string} Persentase dengan 2 desimal dan simbol % atau '-' jika error
 */
export const calculatePercentageByPopulasi = (value, populasiTotal) => {
    try {
        // Konversi ke number
        const valueNum = parseFloat(value) || 0;
        const populasiTotalNum = parseFloat(populasiTotal) || 0;
        
        // Jika populasi total 0, tidak bisa membagi
        if (populasiTotalNum === 0) {
            return '0.00%';
        }
        
        // Hitung persentase: (nilai / populasi total) * 100
        const percentage = (valueNum / populasiTotalNum) * 100;
        
        // Format dengan 2 desimal dan simbol %
        return `${percentage.toFixed(2)}%`;
        
    } catch (error) {
        console.error("Error calculating percentage by populasi:", error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung semua persentase berdasarkan populasi total
 * @param {number|string} earlyCones - Jumlah early cones
 * @param {number|string} dryPetals - Jumlah dry petals
 * @param {number|string} buahAlami - Jumlah buah alami
 * @param {number|string} totalBuahAlami - Total buah alami
 * @param {number|string} populasiTotal - Populasi total
 * @returns {Object} Object berisi semua persentase berdasarkan populasi total

export const calculateAllPercentagesByPopulasi = (earlyCones, dryPetals, buahAlami, totalBuahAlami, populasiTotal) => {
    try {
        // Hitung persentase untuk masing-masing komponen
        const earlyConesPercentage = calculatePercentageByPopulasi(earlyCones, populasiTotal);
        const dryPetalsPercentage = calculatePercentageByPopulasi(dryPetals, populasiTotal);
        const buahAlamiPercentage = calculatePercentageByPopulasi(buahAlami, populasiTotal);
        const totalPercentage = calculatePercentageByPopulasi(totalBuahAlami, populasiTotal);
        
        return {
            earlyConesPercentage,
            dryPetalsPercentage,
            buahAlamiPercentage,
            totalPercentage
        };
        
    } catch (error) {
        console.error("Error calculating all percentages by populasi:", error);
        return {
            earlyConesPercentage: '-',
            dryPetalsPercentage: '-',
            buahAlamiPercentage: '-',
            totalPercentage: '-'
        };
    }
};
 */

export const calculateAllPercentagesByPopulasi = (earlyCones, dryPetals, buahAlami, totalBuahAlami, populasiTotal) => {
    try {
        // KONVERSI SEMUA KE NUMBER DULU!
        const earlyConesNum = parseFloat(earlyCones) || 0;
        const dryPetalsNum = parseFloat(dryPetals) || 0;
        const buahAlamiNum = parseFloat(buahAlami) || 0;
        const totalBuahAlamiNum = parseFloat(totalBuahAlami) || 0;
        const populasiTotalNum = parseFloat(populasiTotal) || 0;
        
        // Sekarang hitung dengan angka yang sudah dikonversi
        const earlyConesPercentage = calculatePercentageByPopulasi(earlyConesNum, populasiTotalNum);
        const dryPetalsPercentage = calculatePercentageByPopulasi(dryPetalsNum, populasiTotalNum);
        const buahAlamiPercentage = calculatePercentageByPopulasi(buahAlamiNum, populasiTotalNum);
        const totalPercentage = calculatePercentageByPopulasi(totalBuahAlamiNum, populasiTotalNum);
        
        return {
            earlyConesPercentage,
            dryPetalsPercentage,
            buahAlamiPercentage,
            totalPercentage
        };
        
    } catch (error) {
        console.error("Error in calculateAllPercentagesByPopulasi:", error);
        return {
            earlyConesPercentage: '-',
            dryPetalsPercentage: '-',
            buahAlamiPercentage: '-',
            totalPercentage: '-'
        };
    }
};

/**
 * Fungsi untuk menghitung Umur - Saat Mapping
 * Formula: Umur - Saat Mapping = Tanggal Selesai Identifikasi - Real Forcing
 * @param {string|Date} tanggalSelesaiIdentifikasi - Tanggal selesai identifikasi
 * @param {string|Date} realForcing - Tanggal real forcing
 * @returns {string|number} Umur HSF saat mapping dalam format desimal atau '-' jika error/real forcing kosong
 */
export const calculateUmurHSFSaatMapping = (tanggalSelesaiIdentifikasi, realForcing) => {
    try {
        // Jika Real Forcing kosong, tampilkan '-'
        if (!realForcing || realForcing === '' || realForcing === '-') {
            return '-';
        }
        
        // Jika Tanggal Selesai Identifikasi kosong, tampilkan '-'
        if (!tanggalSelesaiIdentifikasi || tanggalSelesaiIdentifikasi === '' || tanggalSelesaiIdentifikasi === '-') {
            return '-';
        }
        
        // Parse tanggal dari berbagai format
        const parseDate = (dateString) => {
            let date;
            
            if (typeof dateString === 'string' && dateString.includes('-')) {
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    // Format DD-MM-YYYY atau YYYY-MM-DD
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    
                    // Jika format YYYY-MM-DD (tahun di depan)
                    if (parts[0].length === 4) {
                        date = new Date(parts[0], parts[1] - 1, parts[2]);
                    } else {
                        // Format DD-MM-YYYY
                        date = new Date(year, month, day);
                    }
                } else {
                    date = new Date(dateString);
                }
            } else if (typeof dateString === 'string' && dateString.includes('/')) {
                // Format dengan slash separator
                const parts = dateString.split('/');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    date = new Date(year, month, day);
                } else {
                    date = new Date(dateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            // Validasi tanggal
            if (isNaN(date.getTime())) {
                throw new Error(`Tanggal tidak valid: ${dateString}`);
            }
            
            return date;
        };
        
        const tanggalSelesai = parseDate(tanggalSelesaiIdentifikasi);
        const tanggalRealForcing = parseDate(realForcing);
        
        // Hitung selisih hari
        const diffMs = tanggalSelesai.getTime() - tanggalRealForcing.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        
        // Format ke desimal dengan 2 angka di belakang koma
        return diffDays.toFixed(2);
        
    } catch (error) {
        console.error("Error in calculateUmurHSFSaatMapping:", error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Umur - Saat Ini
 * @param {string|Date} realForcing - Tanggal real forcing
 * @param {string|Date} tanggalClosing - Tanggal closing (opsional)
 * @param {Date} tanggalSaatIni - Tanggal saat ini (default: new Date())
 * @returns {string|number} Umur HSF saat ini dalam format desimal atau '-' jika error
 */
export const calculateUmurHSFSaatIni = (realForcing, tanggalClosing, tanggalSaatIni = new Date()) => {
    try {
        // Jika Real Forcing tidak ada, return '-'
        if (!realForcing || realForcing === '' || realForcing === '-') {
            return '-';
        }
        
        // Parse tanggal Real Forcing
        let parsedRealForcing;
        if (typeof realForcing === 'string' && realForcing.includes('-')) {
            const parts = realForcing.split('-');
            if (parts.length === 3) {
                // Format DD-MM-YYYY
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                parsedRealForcing = new Date(year, month, day);
            } else {
                parsedRealForcing = new Date(realForcing);
            }
        } else {
            parsedRealForcing = new Date(realForcing);
        }
        
        if (isNaN(parsedRealForcing.getTime())) {
            return '-';
        }
        
        // Tentukan tanggal referensi
        let referenceDate;
        
        // Jika Tanggal Closing tidak kosong, gunakan Tanggal Closing
        if (tanggalClosing && tanggalClosing !== '' && tanggalClosing !== '-') {
            // Parse tanggal Closing
            let parsedTanggalClosing;
            if (typeof tanggalClosing === 'string' && tanggalClosing.includes('-')) {
                const parts = tanggalClosing.split('-');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    parsedTanggalClosing = new Date(year, month, day);
                } else {
                    parsedTanggalClosing = new Date(tanggalClosing);
                }
            } else {
                parsedTanggalClosing = new Date(tanggalClosing);
            }
            
            if (!isNaN(parsedTanggalClosing.getTime())) {
                referenceDate = parsedTanggalClosing;
            } else {
                // Jika parsing tanggal closing gagal, gunakan tanggal saat ini
                referenceDate = tanggalSaatIni;
            }
        } else {
            // Jika Tanggal Closing kosong, gunakan tanggal saat ini
            referenceDate = tanggalSaatIni;
        }
        
        // Hitung selisih hari
        const diffMs = referenceDate.getTime() - parsedRealForcing.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        
        // Format ke desimal dengan 2 angka di belakang koma
        return diffDays.toFixed(2);
        
    } catch (error) {
        console.error("Error in calculateUmurHSFSaatIni:", error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Umur (Terbang)
 * Formula: Umur (Terbang) = (mulai_rawat - tanggal_terbang) dalam hari
 * @param {string|Date} mulai_rawat - Tanggal mulai rawat dari tb_lokasi
 * @param {string|Date} tanggal_terbang - Tanggal terbang dari drone data
 * @returns {string|number} Umur terbang dalam format desimal atau '-' jika error

export const calculateUmurTerbang = (mulai_rawat, tanggal_terbang) => {
    try {
        // Jika salah satu tanggal kosong, return '-'
        if (!mulai_rawat || mulai_rawat === '' || mulai_rawat === '-' || 
            !tanggal_terbang || tanggal_terbang === '' || tanggal_terbang === '-') {
            return '-';
        }
        
        // Parse tanggal dari berbagai format
        const parseDate = (dateString) => {
            let date;
            
            if (typeof dateString === 'string' && dateString.includes('-')) {
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    // Format DD-MM-YYYY atau YYYY-MM-DD
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    
                    // Jika format YYYY-MM-DD (tahun di depan)
                    if (parts[0].length === 4) {
                        date = new Date(parts[0], parts[1] - 1, parts[2]);
                    } else {
                        // Format DD-MM-YYYY
                        date = new Date(year, month, day);
                    }
                } else {
                    date = new Date(dateString);
                }
            } else if (typeof dateString === 'string' && dateString.includes('/')) {
                // Format dengan slash separator
                const parts = dateString.split('/');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    date = new Date(year, month, day);
                } else {
                    date = new Date(dateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            // Validasi tanggal
            if (isNaN(date.getTime())) {
                throw new Error(`Tanggal tidak valid: ${dateString}`);
            }
            
            return date;
        };
        
        const tanggalMulaiRawat = parseDate(mulai_rawat);
        const tanggalTerbang = parseDate(tanggal_terbang);
        
        // Hitung selisih hari: mulai_rawat - tanggal_terbang
        const diffMs = tanggalMulaiRawat.getTime() - tanggalTerbang.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        
        // Pastikan hasil positif, jika negatif berarti tanggal_terbang > mulai_rawat
        const absoluteDays = Math.abs(diffDays);
        
        // Format ke desimal dengan 2 angka di belakang koma
        return absoluteDays.toFixed(2);
        
    } catch (error) {
        console.error("Error in calculateUmurTerbang:", error);
        return '-';
    }
};
 */

/**
 * Fungsi untuk menghitung Umur (Terbang)
 * Formula: Umur (Terbang) = (mulai_rawat - tanggal_terbang) dalam hari
 * @param {string|Date} mulai_rawat - Tanggal mulai rawat dari tb_lokasi
 * @param {string|Date} tanggal_terbang - Tanggal terbang dari drone data
 * @returns {Object} Object berisi umur dalam hari dan bulan dengan format: { hari: string, bulan: string, display: string }
 */
export const calculateUmurTerbang = (mulai_rawat, tanggal_terbang) => {
    try {
        // Jika salah satu tanggal kosong, return '-'
        if (!mulai_rawat || mulai_rawat === '' || mulai_rawat === '-' || 
            !tanggal_terbang || tanggal_terbang === '' || tanggal_terbang === '-') {
            return {
                hari: '-',
                bulan: '-',
                display: '-'
            };
        }
        
        // Parse tanggal dari berbagai format
        const parseDate = (dateString) => {
            let date;
            
            if (typeof dateString === 'string' && dateString.includes('-')) {
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    // Format DD-MM-YYYY atau YYYY-MM-DD
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    
                    // Jika format YYYY-MM-DD (tahun di depan)
                    if (parts[0].length === 4) {
                        date = new Date(parts[0], parts[1] - 1, parts[2]);
                    } else {
                        // Format DD-MM-YYYY
                        date = new Date(year, month, day);
                    }
                } else {
                    date = new Date(dateString);
                }
            } else if (typeof dateString === 'string' && dateString.includes('/')) {
                // Format dengan slash separator
                const parts = dateString.split('/');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    date = new Date(year, month, day);
                } else {
                    date = new Date(dateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            // Validasi tanggal
            if (isNaN(date.getTime())) {
                throw new Error(`Tanggal tidak valid: ${dateString}`);
            }
            
            return date;
        };
        
        const tanggalMulaiRawat = parseDate(mulai_rawat);
        const tanggalTerbang = parseDate(tanggal_terbang);
        
        // Hitung selisih hari: mulai_rawat - tanggal_terbang
        const diffMs = tanggalMulaiRawat.getTime() - tanggalTerbang.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        
        // Pastikan hasil positif, jika negatif berarti tanggal_terbang > mulai_rawat
        const absoluteDays = Math.abs(diffDays);
        
        // Hitung umur dalam bulan (rata-rata 30.44 hari per bulan)
        const diffMonths = absoluteDays / 30.44;
        
        // Format ke desimal
        const hariFormatted = absoluteDays.toFixed(0); // Hari tanpa desimal
        const bulanFormatted = diffMonths.toFixed(1);  // Bulan dengan 1 desimal
        
        // Format display: "108 (3,6)"
        const displayFormatted = `${hariFormatted} (${bulanFormatted.replace('.', ',')})`;
        
        return {
            hari: hariFormatted,
            bulan: bulanFormatted,
            display: displayFormatted
        };
        
    } catch (error) {
        console.error("Error in calculateUmurTerbang:", error);
        return {
            hari: '-',
            bulan: '-',
            display: '-'
        };
    }
};

/**
 * Fungsi utama untuk menghitung semua formula berdasarkan data row dengan tambahan mulai_rawat
 * @param {Object} rowData - Data row dari Firebase
 * @param {string} mulai_rawat - Tanggal mulai rawat dari tb_lokasi
 * @returns {Object} Object berisi semua hasil perhitungan termasuk umur_terbang

export const calculateAllFormulas = (rowData, mulai_rawat = null) => {
    if (!rowData) {
        return {
            week: '-',
            totalBuahAlami: '-',
            populasiTotal: '-',
            percentageBuahAlami: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            percentagesByPopulasi: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            umurTerbang: '-'  // Ditambahkan
        };
    }
    
    try {
        // Gunakan fungsi calculateWeekExcel yang sudah diperbaiki
        const week = calculateWeekExcel(rowData.tanggal_terbang);
        
        // Hitung total buah alami
        const totalBuahAlami = calculateTotalBuahAlami(
            rowData.early_cones,
            rowData.dry_petals,
            rowData.buah_alami
        );
        
        // Hitung populasi total KEMBALIKAN ANGKA, bukan string yang sudah diformat
        const populasiTotal = calculatePopulasiTotal(
            rowData.populasi_per_ha,
            rowData.luas_netto
        );
        
        // Hitung persentase buah alami (persentase internal)
        const percentageBuahAlami = calculatePercentageBuahAlami(
            rowData.early_cones,
            rowData.dry_petals,
            rowData.buah_alami
        );
        
        // Hitung persentase berdasarkan populasi total
        // Parse total buah alami untuk perhitungan
        const totalBuahAlamiNum = parseFloat(rowData.early_cones || 0) + 
                                  parseFloat(rowData.dry_petals || 0) + 
                                  parseFloat(rowData.buah_alami || 0);
        
        const percentagesByPopulasi = calculateAllPercentagesByPopulasi(
            rowData.early_cones,
            rowData.dry_petals,
            rowData.buah_alami,
            totalBuahAlamiNum,
            populasiTotal
        );
        
        // Hitung umur terbang jika mulai_rawat tersedia
        const umurTerbang = mulai_rawat ? 
            calculateUmurTerbang(mulai_rawat, rowData.tanggal_terbang) : '-';
        
        return {
            week,
            totalBuahAlami,
            populasiTotal,
            percentageBuahAlami,
            percentagesByPopulasi,
            umurTerbang  // Ditambahkan
        };
        
    } catch (error) {
        console.error("Error in calculateAllFormulas:", error);
        return {
            week: '-',
            totalBuahAlami: '-',
            populasiTotal: '-',
            percentageBuahAlami: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            percentagesByPopulasi: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            umurTerbang: '-'  // Ditambahkan
        };
    }
};
 */

/**
 * Fungsi utama untuk menghitung semua formula berdasarkan data row dengan tambahan mulai_rawat
 * @param {Object} rowData - Data row dari Firebase
 * @param {string} mulai_rawat - Tanggal mulai rawat dari tb_lokasi
 * @returns {Object} Object berisi semua hasil perhitungan termasuk umur_terbang
 */
export const calculateAllFormulas = (rowData, mulai_rawat = null) => {
    if (!rowData) {
        return {
            week: '-',
            totalBuahAlami: '-',
            populasiTotal: '-',
            percentageBuahAlami: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            percentagesByPopulasi: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            umurTerbang: {  // Diubah menjadi object
                hari: '-',
                bulan: '-',
                display: '-'
            }
        };
    }
    
    try {
        // Gunakan fungsi calculateWeekExcel yang sudah diperbaiki
        const week = calculateWeekExcel(rowData.tanggal_terbang);
        
        // Hitung total buah alami
        const totalBuahAlami = calculateTotalBuahAlami(
            rowData.early_cones,
            rowData.dry_petals,
            rowData.buah_alami
        );
        
        // Hitung populasi total KEMBALIKAN ANGKA, bukan string yang sudah diformat
        const populasiTotal = calculatePopulasiTotal(
            rowData.populasi_per_ha,
            rowData.luas_netto
        );
        
        // Hitung persentase buah alami (persentase internal)
        const percentageBuahAlami = calculatePercentageBuahAlami(
            rowData.early_cones,
            rowData.dry_petals,
            rowData.buah_alami
        );
        
        // Hitung persentase berdasarkan populasi total
        // Parse total buah alami untuk perhitungan
        const totalBuahAlamiNum = parseFloat(rowData.early_cones || 0) + 
                                  parseFloat(rowData.dry_petals || 0) + 
                                  parseFloat(rowData.buah_alami || 0);
        
        const percentagesByPopulasi = calculateAllPercentagesByPopulasi(
            rowData.early_cones,
            rowData.dry_petals,
            rowData.buah_alami,
            totalBuahAlamiNum,
            populasiTotal
        );
        
        // Hitung umur terbang jika mulai_rawat tersedia
        const umurTerbang = mulai_rawat ? 
            calculateUmurTerbang(mulai_rawat, rowData.tanggal_terbang) : {
                hari: '-',
                bulan: '-',
                display: '-'
            };
        
        return {
            week,
            totalBuahAlami,
            populasiTotal,
            percentageBuahAlami,
            percentagesByPopulasi,
            umurTerbang
        };
        
    } catch (error) {
        console.error("Error in calculateAllFormulas:", error);
        return {
            week: '-',
            totalBuahAlami: '-',
            populasiTotal: '-',
            percentageBuahAlami: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            percentagesByPopulasi: {
                earlyConesPercentage: '-',
                dryPetalsPercentage: '-',
                buahAlamiPercentage: '-',
                totalPercentage: '-'
            },
            umurTerbang: {
                hari: '-',
                bulan: '-',
                display: '-'
            }
        };
    }
};


/**
 * Fungsi untuk testing format populasi total
 */
export const testPopulasiTotalFormat = () => {
    const testCases = [
        { populasiPerHa: 65736.1, luasNetto: 10, expected: 657361 },
        { populasiPerHa: 332, luasNetto: 1, expected: 332 },
        { populasiPerHa: 1234.56, luasNetto: 2.5, expected: 3086.4 },
        { populasiPerHa: '65736.1', luasNetto: '10', expected: 657361 },
        { populasiPerHa: '332', luasNetto: '1', expected: 332 },
        { populasiPerHa: 0, luasNetto: 10, expected: 0 },
        { populasiPerHa: '', luasNetto: '10', expected: 0 },
    ];
    
    console.log('Testing populasi total (return as number):');
    testCases.forEach((testCase, index) => {
        const result = calculatePopulasiTotal(testCase.populasiPerHa, testCase.luasNetto);
        console.log(`Test ${index + 1}: ${testCase.populasiPerHa} * ${testCase.luasNetto} = ${result} (expected: ${testCase.expected})`);
        console.log(`   Type: ${typeof result}`);
    });
};

// Ekspor default object dengan semua fungsi
export default {
    calculateWeek,
    calculateWeekExcel,
    calculateTotalBuahAlami,
    calculatePopulasiTotal,
    calculatePopulasiTotalExact,
    calculatePercentageBuahAlami,
    calculatePercentageByPopulasi,
    calculateAllPercentagesByPopulasi,
    calculateUmurHSFSaatMapping,
    calculateUmurHSFSaatIni,
    calculateUmurTerbang,
    calculateAllFormulas,
    testPopulasiTotalFormat
};